// ── IconButton — кнопка-значок ───────────────────────────────────────────────
// Название (label) обязательно: оно же подсказка при наведении и имя для
// экранного диктора — безымянной кнопки-значка быть не может.
// Варианты: outline (рамка, как в шапке), ghost (без рамки), filled (синяя; в теме с
// фишками — круглая покерная фишка, chipFace в surface.js).
// Размеры: 32 · 36 · 40 · 44. Если меньше 44 — на сенсорном экране зона нажатия
// невидимо расширяется до 44 px (ac-hit), внешний вид не меняется.

import { forwardRef } from 'react';
import Icon from '../Icon';
import { SANS } from '../themes';
import { labelOn, readableOn, shade } from './color';
import { chipFace } from './surface';
import { usePalette } from './theme';

const IconButton = forwardRef(function IconButton({
  icon, label, size = 40, variant = 'outline', active = false, danger = false, iconSize, badge,
  children, style, className = '', C: Cp, type = 'button', ...rest
}, ref) {
  const C = usePalette(Cp);
  if (process.env.NODE_ENV !== 'production' && !label) console.warn('IconButton без label:', icon);
  const filled = variant === 'filled';
  const fill = danger ? (C.dangerFill || C.danger) : C.blue;
  const label_ = filled ? labelOn(C, fill) : null;
  const fg = filled ? label_.fg : active ? C.blueDark : danger ? C.danger : C.mid;
  // Обводка на живом столе «Аркады» — со стеклянной подложкой, а не прозрачная.
  const bg = filled ? fill : active ? C.bluePale : variant === 'outline' && C.lift ? C.surface : 'transparent';
  const bd = variant === 'outline' ? (active ? C.blue : C.border) : filled ? bg : 'transparent';
  // Объёмная тема: заливная и обведённая кнопки получают «ребро» снизу и блик (см. Button и
  // index.css), обведённая — ещё и кристальную грань; прозрачная (ghost) остаётся значком.
  const lift = !!C.lift && variant !== 'ghost';
  const chip = filled ? chipFace(C, { danger, round: true }) : null;
  return (
    <button ref={ref} type={type} title={label} aria-label={label} aria-pressed={active || undefined}
      data-lift={lift && !chip ? '' : undefined} data-outline={lift && !chip && label_?.outline ? '' : undefined} {...chip?.attrs}
      data-crystal={lift && variant === 'outline' ? '' : undefined}
      className={[filled ? 'ac-press' : 'ac-ibtn', size < 44 ? 'ac-hit' : '', className].filter(Boolean).join(' ')}
      style={{ width: size, height: size, flexShrink: 0, display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        borderRadius: size >= 40 ? 8 : 7, border: `${variant === 'ghost' ? 1 : C.borderW || 1}px solid ${bd}`, background: bg, color: fg, cursor: 'pointer', padding: 0,
        '--ac-edge': lift ? shade(bg) : undefined,
        fontFamily: SANS, position: 'relative', boxSizing: 'border-box', ...chip?.style, ...style,
        // фишка всегда круглая, даже если место задаёт свою форму (шапка, меню)
        ...(chip ? { borderRadius: '50%' } : null) }}
      {...rest}>
      {children || <Icon n={icon} s={iconSize || Math.round(size * 0.45)} />}
      {badge != null && badge !== false && badge !== 0 && (
        <span aria-hidden="true" style={{ position: 'absolute', top: -5, right: -5, minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9,
          background: C.blue, color: readableOn(C.blue), fontSize: 11, fontWeight: 700, lineHeight: '18px', textAlign: 'center',
          boxShadow: `0 0 0 2px ${C.pageBg}`, boxSizing: 'border-box', pointerEvents: 'none' }}>{badge}</span>
      )}
    </button>
  );
});

export default IconButton;
